import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Check, ExternalLink, FileText, Folder, Loader2, RefreshCw, ShieldCheck } from 'lucide-react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

// Fallbacks until the status loads; the backend catalogue is the source of truth for every source.
const RESOURCE_META = {
  sharepoint: { label: 'SharePoint', provider: 'microsoft365', account: 'Microsoft 365', auth: 'oauth' },
  onedrive: { label: 'OneDrive', provider: 'microsoft365', account: 'Microsoft 365', auth: 'oauth' },
  google_drive: { label: 'Google Drive', provider: 'google_drive', account: 'Google', auth: 'oauth' },
};

const errorMessage = (error, fallback) => typeof error?.response?.data?.detail === 'string' ? error.response.data.detail : fallback;

const CloudStorageConnectorDialog = ({ open, onOpenChange, resource, teams = [], onImported, canConnect = false }) => {
  const [status, setStatus] = useState(null);
  const meta = status?.resources?.[resource] || RESOURCE_META[resource] || RESOURCE_META.sharepoint;
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [path, setPath] = useState([]);
  const [selected, setSelected] = useState([]);
  const [syncInterval, setSyncInterval] = useState('manual_check_only');
  const [teamId, setTeamId] = useState('');
  const [assetType, setAssetType] = useState('note');
  const [showInHub, setShowInHub] = useState(true);
  const [accessFields, setAccessFields] = useState({});
  const [preview, setPreview] = useState(null);
  const [busyAction, setBusyAction] = useState('');

  const providerStatus = status?.providers?.[meta.provider];
  const connector = providerStatus?.connector;
  const connected = Boolean(providerStatus?.connected);
  const currentFolder = path[path.length - 1];

  const resourceSelections = useMemo(
    () => selected.filter(item => item.resource === resource),
    [selected, resource]
  );

  const loadStatus = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/cloud-connectors/status');
      setStatus(response.data);
      const provider = response.data?.resources?.[resource]?.provider || meta.provider;
      const saved = response.data?.providers?.[provider]?.connector;
      setSelected(saved?.locations || []);
      setSyncInterval(saved?.sync_interval || 'manual_check_only');
      setTeamId(saved?.team_id || '');
      setAssetType(saved?.asset_type || 'note');
      setShowInHub(saved?.show_in_hub !== false);
    } catch (error) {
      toast.error(errorMessage(error, 'Could not load connector status'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) { setAccessFields({}); return; }
    setItems([]);
    setPath([]);
    setPreview(null);
    setAccessFields({});
    loadStatus();
  }, [open, resource]);

  // Storage connected with read-only access pasted from IT (Amazon S3, Azure Blob).
  const connectWithAccess = async (event) => {
    event.preventDefault();
    setBusyAction('connect');
    try {
      const response = await axiosInstance.post(`/cloud-connectors/${meta.provider}/credentials`, { fields: accessFields });
      toast.success(`${meta.label} connected · ${response.data.visible_items} item${response.data.visible_items === 1 ? '' : 's'} visible. Choose folders to sync.`);
      setAccessFields({});
      await loadStatus();
    } catch (error) {
      toast.error(errorMessage(error, `Could not connect ${meta.label}`));
    } finally {
      setBusyAction('');
    }
  };

  const connect = async () => {
    setBusyAction('connect');
    try {
      const response = await axiosInstance.post(`/cloud-connectors/${meta.provider}/authorize`);
      window.location.assign(response.data.authorization_url);
    } catch (error) {
      toast.error(errorMessage(error, `Could not connect ${meta.account}`));
      setBusyAction('');
    }
  };

  const disconnect = async () => {
    if (!window.confirm(`Disconnect ${meta.label}? Qflow stops reading it and forgets the access. Knowledge already imported stays.`)) return;
    setBusyAction('disconnect');
    try {
      await axiosInstance.delete(`/cloud-connectors/${meta.provider}`);
      toast.success(`${meta.label} disconnected`);
      setItems([]);
      setPath([]);
      await loadStatus();
    } catch (error) {
      toast.error(errorMessage(error, `Could not disconnect ${meta.label}`));
    } finally {
      setBusyAction('');
    }
  };

  const loadRoots = async () => {
    setBusyAction('browse');
    setPreview(null);
    try {
      const response = await axiosInstance.get(`/cloud-connectors/${resource}/roots`);
      setItems(response.data.items || []);
      setPath([]);
    } catch (error) {
      toast.error(errorMessage(error, `Could not browse ${meta.label}`));
    } finally {
      setBusyAction('');
    }
  };

  const openFolder = async (folder) => {
    setBusyAction(`folder:${folder.drive_id || ''}:${folder.id}`);
    setPreview(null);
    try {
      const response = await axiosInstance.get(`/cloud-connectors/${resource}/children`, {
        params: { item_id: folder.id, drive_id: folder.drive_id || undefined },
      });
      setItems(response.data.items || []);
      setPath(currentFolder ? [...path, folder] : [folder]);
    } catch (error) {
      toast.error(errorMessage(error, `Could not open ${folder.name}`));
    } finally {
      setBusyAction('');
    }
  };

  const goBack = async () => {
    if (path.length <= 1) {
      await loadRoots();
      return;
    }
    const parentPath = path.slice(0, -1);
    const parent = parentPath[parentPath.length - 1];
    setBusyAction('back');
    try {
      const response = await axiosInstance.get(`/cloud-connectors/${resource}/children`, {
        params: { item_id: parent.id, drive_id: parent.drive_id || undefined },
      });
      setItems(response.data.items || []);
      setPath(parentPath);
    } catch (error) {
      toast.error(errorMessage(error, 'Could not return to the previous folder'));
    } finally {
      setBusyAction('');
    }
  };

  const selectionKey = item => `${item.resource}:${item.drive_id || ''}:${item.id}`;
  const isSelected = item => selected.some(value => selectionKey(value) === selectionKey(item));
  const toggleSelected = item => {
    setSelected(current => isSelected(item)
      ? current.filter(value => selectionKey(value) !== selectionKey(item))
      : [...current, item]
    );
  };

  const previewFile = async (item) => {
    setBusyAction(`preview:${item.id}`);
    try {
      const response = await axiosInstance.post(`/cloud-connectors/${resource}/preview`, { item });
      setPreview(response.data);
    } catch (error) {
      toast.error(errorMessage(error, `Could not preview ${item.name}`));
    } finally {
      setBusyAction('');
    }
  };

  const save = async (syncNow = false) => {
    setBusyAction(syncNow ? 'sync' : 'save');
    try {
      await axiosInstance.put(`/cloud-connectors/${meta.provider}/settings`, {
        locations: selected,
        sync_interval: syncInterval,
        team_id: teamId || null,
        asset_type: assetType,
        show_in_hub: showInHub,
      });
      if (syncNow) {
        const response = await axiosInstance.post(`/cloud-connectors/${meta.provider}/sync`);
        const summary = response.data;
        toast.success(`Sync complete: ${summary.imported || 0} imported, ${summary.unchanged || 0} unchanged, ${summary.change_pending_review || 0} awaiting review${summary.failed ? `, ${summary.failed} failed` : ''}.`);
        onImported?.();
      } else {
        toast.success(`${meta.label} sync settings saved`);
      }
      await loadStatus();
    } catch (error) {
      toast.error(errorMessage(error, syncNow ? 'Cloud sync failed' : 'Could not save connector settings'));
    } finally {
      setBusyAction('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid={`cloud-connector-${resource}-dialog`}>
        <DialogHeader>
          <DialogTitle>{meta.label} knowledge connector</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-sm text-gray-500"><Loader2 className="mr-2 animate-spin" size={18} /> Loading connector</div>
        ) : !providerStatus?.configured && !connected ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="connector-not-ready">
            <p className="font-semibold">Not available yet</p>
            <p className="mt-1">Qflow needs to finish setting up this source. Contact Qflow support to make {meta.label} available.</p>
          </div>
        ) : !connected && !canConnect ? (
          <p className="text-sm">Ask your Tenant Admin to connect this source.</p>
        ) : !connected && meta.auth === 'credentials' ? (
          <form onSubmit={connectWithAccess} className="space-y-4" data-testid="connector-access-form">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
              <div className="flex gap-2"><ShieldCheck className="mt-0.5 shrink-0" size={18} /><div><p className="font-semibold">Connect {meta.label} with read-only access</p><p className="mt-1">{meta.setup_help} Qflow tests the access with a read before saving it, stores it encrypted, and never shows it again. Personal passwords are never given to Qflow.</p></div></div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {(meta.fields || []).map(field => (
                <label key={field.key} className={`text-xs font-medium ${field.type === 'secret' || field.key.endsWith('url') ? 'sm:col-span-2' : ''}`} style={{ color: 'var(--qflow-text)' }}>{field.label}
                  <input
                    type={field.type === 'secret' ? 'password' : 'text'}
                    autoComplete="off"
                    required={field.required}
                    value={accessFields[field.key] || ''}
                    onChange={event => setAccessFields(current => ({ ...current, [field.key]: event.target.value }))}
                    placeholder={field.placeholder || ''}
                    className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                    style={{ background: 'var(--qflow-app-bg)', borderColor: 'var(--qflow-border)', color: 'var(--qflow-text)' }}
                    data-testid={`connector-field-${field.key}`}
                  />
                  {field.help && <span className="mt-1 block font-normal" style={{ color: 'var(--qflow-muted)' }}>{field.help}</span>}
                </label>
              ))}
            </div>
            <Button type="submit" disabled={busyAction === 'connect'}>
              {busyAction === 'connect' ? <Loader2 size={15} className="mr-2 animate-spin" /> : <ShieldCheck size={15} className="mr-2" />}
              Test and connect
            </Button>
          </form>
        ) : !connected ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
              <div className="flex gap-2"><ShieldCheck className="mt-0.5 shrink-0" size={18} /><div><p className="font-semibold">Connect {meta.account} with read-only access</p><p className="mt-1">You will be sent to {meta.account} to approve access. Passwords are never given to Qflow; the returned access token is encrypted before storage.</p></div></div>
            </div>
            <Button onClick={connect} disabled={busyAction === 'connect'}>
              {busyAction === 'connect' ? <Loader2 size={15} className="mr-2 animate-spin" /> : <ExternalLink size={15} className="mr-2" />}
              Connect {meta.account}
            </Button>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm">
              <div><p className="font-semibold text-emerald-900">Connected to {connector?.display_name || meta.account}</p><p className="text-xs text-emerald-700">{connector?.account_email || 'Read-only cloud account access'}</p></div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={loadRoots} disabled={Boolean(busyAction)}>
                  {busyAction === 'browse' ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Folder size={14} className="mr-1.5" />} Choose folders or files
                </Button>
                {canConnect && <Button variant="ghost" size="sm" onClick={disconnect} disabled={Boolean(busyAction)} className="text-rose-700" data-testid="connector-disconnect">Disconnect</Button>}
              </div>
            </div>

            {(items.length > 0 || currentFolder) && (
              <div className="rounded-lg border">
                <div className="flex items-center gap-2 border-b bg-gray-50 px-3 py-2">
                  {currentFolder && <button type="button" onClick={goBack} className="rounded p-1 hover:bg-gray-200" aria-label="Back"><ArrowLeft size={16} /></button>}
                  <p className="min-w-0 flex-1 truncate text-sm font-medium text-gray-800">{currentFolder?.name || `${meta.label} locations`}</p>
                  {currentFolder && <Button variant="outline" size="sm" onClick={() => toggleSelected(currentFolder)}>{isSelected(currentFolder) ? <Check size={14} className="mr-1" /> : <Folder size={14} className="mr-1" />}{isSelected(currentFolder) ? 'Selected' : 'Use this folder'}</Button>}
                </div>
                <div className="max-h-64 divide-y overflow-y-auto">
                  {items.map(item => (
                    <div key={selectionKey(item)} className="flex items-center gap-2 px-3 py-2 text-sm">
                      {item.kind === 'folder' ? <Folder size={16} className="shrink-0 text-amber-500" /> : <FileText size={16} className="shrink-0 text-blue-500" />}
                      <span className="min-w-0 flex-1 truncate">{item.name}</span>
                      {item.kind === 'folder' ? (
                        <Button variant="ghost" size="sm" onClick={() => openFolder(item)} disabled={Boolean(busyAction)}>Open</Button>
                      ) : (
                        <><Button variant="ghost" size="sm" onClick={() => previewFile(item)} disabled={Boolean(busyAction)}>Preview</Button><Button variant="outline" size="sm" onClick={() => toggleSelected(item)}>{isSelected(item) ? 'Selected' : 'Select'}</Button></>
                      )}
                    </div>
                  ))}
                  {!items.length && <p className="px-3 py-8 text-center text-xs text-gray-500">This folder is empty.</p>}
                </div>
              </div>
            )}

            {preview && (
              <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3">
                <div className="flex items-center justify-between gap-3"><p className="truncate text-sm font-semibold text-gray-800">Preview: {preview.name}</p><span className="text-[10px] text-gray-500">{preview.modified_at ? `Modified ${new Date(preview.modified_at).toLocaleString()}` : ''}</span></div>
                <pre className="mt-2 max-h-52 overflow-auto whitespace-pre-wrap rounded bg-white p-3 text-xs leading-relaxed text-gray-700">{preview.preview || 'This file has no text preview.'}</pre>
                {preview.truncated && <p className="mt-1 text-[10px] text-gray-500">Preview shortened. The full document will be imported.</p>}
              </div>
            )}

            <div className="grid gap-3 rounded-lg border bg-gray-50 p-3 sm:grid-cols-3">
              <label className="text-xs font-medium text-gray-700">Check for changes
                <select value={syncInterval} onChange={event => setSyncInterval(event.target.value)} className="mt-1 w-full rounded-md border border-gray-200 bg-white px-2 py-2 text-sm">
                  <option value="manual_check_only">Manual only</option><option value="daily">Daily</option><option value="weekly">Weekly</option>
                </select>
              </label>
              <label className="text-xs font-medium text-gray-700">Knowledge type
                <select value={assetType} onChange={event => setAssetType(event.target.value)} className="mt-1 w-full rounded-md border border-gray-200 bg-white px-2 py-2 text-sm">
                  <option value="note">Note</option><option value="policy">Policy</option><option value="process">Process</option><option value="resolution">Resolution guide</option>
                </select>
              </label>
              <label className="text-xs font-medium text-gray-700">Assign to team
                <select value={teamId} onChange={event => setTeamId(event.target.value)} className="mt-1 w-full rounded-md border border-gray-200 bg-white px-2 py-2 text-sm">
                  <option value="">No team</option>{teams.map(team => <option key={team.id} value={team.id}>{team.name}</option>)}
                </select>
              </label>
            </div>

            <label className="flex items-start gap-3 rounded-lg border bg-gray-50 p-3 text-sm" data-testid="connector-show-in-hub">
              <input type="checkbox" checked={showInHub} onChange={event => setShowInHub(event.target.checked)} className="mt-0.5 h-4 w-4 accent-blue-600" />
              <span><span className="block font-medium text-gray-800">Show as a shortcut in the Knowledge Hub</span><span className="block text-xs text-gray-500">People who manage knowledge can open it, sync it now and see what came in.</span></span>
            </label>

            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-auto text-xs text-gray-500">{resourceSelections.length} {meta.label} location{resourceSelections.length === 1 ? '' : 's'} selected. Changed files create a reviewable comparison before publishing.</span>
              <Button variant="outline" onClick={() => save(false)} disabled={Boolean(busyAction)}>{busyAction === 'save' && <Loader2 size={14} className="mr-1.5 animate-spin" />}Save settings</Button>
              <Button onClick={() => save(true)} disabled={Boolean(busyAction) || selected.length === 0}>{busyAction === 'sync' ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <RefreshCw size={14} className="mr-1.5" />}Save and sync now</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CloudStorageConnectorDialog;
